import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { enumSubscriptionType } from '@app/data-layer/enums/common';
import { enumOrganizationRole, enumOrganizationSize, enumOrganizationType } from '@app/data-layer/enums/organizations';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { DocumentAccessCredentialsModel } from '@app/data-layer/models/documents';
import { OrganizationMemberModel } from '@app/data-layer/models/organizations';
import { OrganizationModel } from '@app/data-layer/models/organizations/organization-model';
import { UserBetaFlagModel, UserModel } from '@app/data-layer/models/user';
import { DocumentsRepository } from '@app/data-layer/repositories';
import { OrganizationLogoRepository } from '@app/data-layer/repositories/organization-logo.repository';
import { SharedSelectors } from '@app/shared/shared-state';
import { Select } from '@ngxs/store';
import { GenericService } from '@services/generic.service';
import { OrganizationsService } from '@services/organizations.service';
import { Observable, ReplaySubject } from 'rxjs';
import { take, map, switchMap, takeUntil, finalize } from 'rxjs/operators';

@Component({
  selector: 'organization-details-card',
  templateUrl: './organization-details-card.component.html',
  styleUrls: ['./organization-details-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationDetailsCardComponent implements OnInit, OnDestroy {
  @Select(SharedSelectors.getAppBetaFlags) public appBetaFlags$: Observable<UserBetaFlagModel[]>;

  public enumOrganizationType: typeof enumOrganizationType = enumOrganizationType;
  public enumOrganizationSize: typeof enumOrganizationSize = enumOrganizationSize;
  public enumSubscriptionType: typeof enumSubscriptionType = enumSubscriptionType;
  public isTablet$: Observable<boolean>;
  public isLoadingLogo: boolean;
  public isHasOnboardingRedesign: boolean = false;

  private currentUserMember: OrganizationMemberModel;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  @Input() public organization: OrganizationModel;
  @Input() public currentUser: UserModel;
  @Input() public organizationMembers: OrganizationMemberModel[];

  constructor(
    private organizationLogoRepository: OrganizationLogoRepository,
    private organizationsService: OrganizationsService,
    private documentsRepository: DocumentsRepository,
    private genericService: GenericService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnInit() {
    this.isTablet$ = this.genericService.isTablet$;
    this.currentUserMember = this.organizationMembers?.find((member) => member.user.userId === this.currentUser.uid);

    this.appBetaFlags$.pipe(takeUntil(this.destroy$)).subscribe((flags: UserBetaFlagModel[]) => {
      this.isHasOnboardingRedesign = flags.map((flag: UserBetaFlagModel) => flag.tag).includes(enumUserBetaFlag.onboardingRedesign);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get isAdmin(): boolean {
    return this.currentUserMember?.role === enumOrganizationRole.ADMIN;
  }

  public redirectToOrganizationEdit(organizationId: number) {
    this.router.navigate(['/companies', 'account-settings', 'organization-view', organizationId]);
  }

  public uploadOrganizationLogo(file: File, organizationId: number): void {
    this.isLoadingLogo = true;
    this.uploadLogoToServer(file, organizationId)
      .pipe(
        finalize(() => (this.isLoadingLogo = true)),
        takeUntil(this.destroy$),
      )
      .subscribe((organization) => {
        this.organization = organization;
        this.cdr.markForCheck();
      });
  }

  public getOrganizationAddressLine(organization: OrganizationModel): string {
    return this.organizationsService.getOrganizationAddressLine(organization);
  }

  public getPlanTypeName(type: enumSubscriptionType): string {
    const planName = type ? `LABELS.PLAN.${enumSubscriptionType[type]}.NAME` : 'LABELS.PLAN.BASIC.NAME';
    return planName;
  }

  private uploadLogoToServer(file: File, organizationId: number): Observable<OrganizationModel> {
    return this.organizationLogoRepository.getCredentials(this.organization.id).pipe(
      switchMap((credentials: DocumentAccessCredentialsModel) => {
        return this.documentsRepository.uploadFileByUrl(credentials.uploadUrl, file, false).pipe(map(() => credentials));
      }),
      switchMap((credentials) => {
        return this.organizationLogoRepository.setUrl(organizationId, credentials.downloadUrl);
      }),
      switchMap(() => this.organizationsService.getOrganizationByIdFromServer(organizationId)),
      take(1),
    );
  }
}
